import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

import type { Scope } from "@busright/data-contract";

import { FLEET_SCOPE, scopeEquals } from "@/lib/scope";

// =================================================================================================
// ScopeContext — "where am I looking" for the whole hub.
//
// Holds the current `Scope` (the district switcher + every filter set this) plus an arbitrary
// string→string `filters` map (narrowing WITHIN the scope, e.g. { riskTier: "CRITICAL" }). The
// contract says a filter is just narrowing scope and a drill is moving to a deeper level — both
// flow through here. Default scope is the fleet (everything). Surfaces read it with `useScope()`.
//
// This is intentionally a single shared scope for the shell-level switcher. A surface that wants a
// LOCAL scope (e.g. a drawer drilling independently) keeps its own React state and does not touch
// this context — the engine's `DatasetDrawer` does exactly that for its breadcrumb stack.
// =================================================================================================

export type ScopeFilters = Readonly<Record<string, string>>;

export interface ScopeContextValue {
  readonly scope: Scope;
  readonly filters: ScopeFilters;
  // Replace the current scope (e.g. the district switcher picks a district). Resets filters when
  // moving to a different scope target so stale narrowing doesn't leak across scopes.
  readonly setScope: (scope: Scope) => void;
  // Set/clear one filter key. Passing `null` removes it.
  readonly setFilter: (key: string, value: string | null) => void;
  // Clear all filters at the current scope.
  readonly clearFilters: () => void;
  // Reset to the fleet root with no filters.
  readonly resetScope: () => void;
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

export interface ScopeProviderProps {
  readonly children: ReactNode;
  // Optional starting scope (defaults to fleet) — handy for tests / deep links.
  readonly initialScope?: Scope;
}

export const ScopeProvider = ({ children, initialScope }: ScopeProviderProps): ReactNode => {
  const [scope, setScopeState] = useState<Scope>(initialScope ?? FLEET_SCOPE);
  const [filters, setFilters] = useState<ScopeFilters>({});

  const setScope = useCallback((next: Scope): void => {
    setScopeState((prev) => {
      // Moving to a genuinely different scope target clears filters; re-setting the same scope
      // (e.g. attaching a freshly-resolved label) keeps them.
      if (!scopeEquals(prev, next)) {
        setFilters({});
      }
      return next;
    });
  }, []);

  const setFilter = useCallback((key: string, value: string | null): void => {
    setFilters((prev) => {
      if (value === null) {
        if (!(key in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const clearFilters = useCallback((): void => setFilters({}), []);

  const resetScope = useCallback((): void => {
    setScopeState(FLEET_SCOPE);
    setFilters({});
  }, []);

  const value = useMemo<ScopeContextValue>(
    () => ({ scope, filters, setScope, setFilter, clearFilters, resetScope }),
    [scope, filters, setScope, setFilter, clearFilters, resetScope],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

// Read the current scope + filters. Throws if used outside a ScopeProvider so a missing provider is
// a loud bug, never a silent fleet-scope fallback.
export const useScope = (): ScopeContextValue => {
  const ctx = useContext(ScopeContext);
  if (ctx === null) {
    throw new Error("useScope must be used within a <ScopeProvider>");
  }
  return ctx;
};
